<!-- Immersive Hero Section -->
<section class="relative h-[60vh] md:h-[70vh] w-full overflow-hidden">
	<!-- Gradient Background -->
	<div class="absolute inset-0 bg-linear-to-br from-charcoal via-primary to-charcoal"></div>

	<!-- Decorative Elements -->
	<div class="absolute inset-0 overflow-hidden pointer-events-none">
		<div class="absolute top-1/4 -left-32 w-[500px] h-[500px] bg-secondary/15 rounded-full blur-3xl animate-pulse"></div>
		<div class="absolute bottom-1/4 -right-32 w-[600px] h-[600px] bg-secondary/10 rounded-full blur-3xl animate-pulse" style="animation-delay: 1.5s;"></div>
		<div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-white/5 rounded-full blur-3xl"></div>
	</div>

	<!-- Grid Pattern -->
	<div class="absolute inset-0 opacity-10" style="background-image: linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px); background-size: 60px 60px;"></div>

	<!-- Diagonal Lines Pattern -->
	<div class="absolute inset-0 opacity-5" style="background-image: repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.1) 35px, rgba(255,255,255,.1) 70px);"></div>

	<!-- Content - Centered -->
	<div class="relative z-10 h-full flex flex-col items-center justify-center text-center px-6 md:px-16 lg:px-20">
		<!-- Decorative Line -->
		<div class="flex items-center gap-4 mb-8">
			<div class="w-16 h-px bg-gradient-to-r from-transparent to-secondary"></div>
			<span class="text-secondary text-sm font-semibold tracking-[0.3em] uppercase">{{ heroTagline() }}</span>
			<div class="w-16 h-px bg-gradient-to-l from-transparent to-secondary"></div>
		</div>

		<!-- Main Heading -->
		<h1 class="font-cenzo text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-[0.95] mb-6">
			{{ heroTitle() }}
		</h1>

		<!-- Description -->
		<p class="text-white/70 text-lg md:text-xl max-w-2xl leading-relaxed mb-10">
			{{ heroDescription() }}
		</p>

		<!-- CTA Buttons -->
		<div class="flex flex-wrap justify-center gap-4">
			<button
				(click)="openBookingModal()"
				class="group px-8 py-4 bg-secondary text-primary rounded-full font-bold text-lg tracking-wide shadow-xl hover:shadow-secondary/30 hover:scale-105 transition-all duration-300">
				<span class="flex items-center gap-2">
					Book Consultation
				</span>
			</button>
			<a [href]="'tel:' + heroPhone()"
				class="px-8 py-4 bg-white/10 backdrop-blur-sm border border-white/30 text-white rounded-full font-semibold text-lg hover:bg-white/20 transition-all duration-300 flex items-center gap-3">
				<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
				</svg>
				Call Us Now
			</a>
		</div>
	</div>
</section>

<!-- Category Selection with Large Images -->
<section class="relative py-20 md:py-28 bg-mist overflow-hidden">
	<!-- Background Pattern -->
	<div class="absolute inset-0 opacity-[0.03]" style="background-image: radial-gradient(circle at 1px 1px, #5d202d 1px, transparent 0); background-size: 40px 40px;"></div>

	<div class="relative max-w-[1600px] mx-auto px-6 md:px-16 lg:px-20">
		<!-- Section Header -->
		<div class="text-center mb-16">
			<div class="inline-flex items-center gap-4 mb-6">
				<div class="w-12 h-px bg-gradient-to-r from-transparent to-secondary"></div>
				<span class="text-secondary text-sm font-semibold tracking-[0.3em] uppercase">{{ categoriesTagline() }}</span>
				<div class="w-12 h-px bg-gradient-to-l from-transparent to-secondary"></div>
			</div>
			<h2 class="font-cenzo text-4xl md:text-5xl lg:text-6xl font-bold text-primary mb-4">
				{{ categoriesTitle() }}
			</h2>
			<p class="text-charcoal/60 text-lg max-w-2xl mx-auto">
				{{ categoriesDescription() }}
			</p>
		</div>

		@if (isLoading()) {
			<!-- Loading State -->
			<div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
				@for (i of [1,2,3]; track i) {
					<div class="h-[400px] rounded-3xl bg-charcoal/5 animate-pulse"></div>
				}
			</div>
		} @else {
			<!-- Category Cards Grid -->
			<div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 justify-items-center">
				@for (category of categories(); track category.id) {
					<button
						(click)="selectCategory(category)"
						class="group relative h-[420px] rounded-3xl overflow-hidden text-left transition-all duration-700 ease-out cursor-pointer w-full max-w-md"
						[class.ring-4]="activeCategory()?.id === category.id"
						[class.ring-secondary]="activeCategory()?.id === category.id"
						[class.ring-offset-4]="activeCategory()?.id === category.id"
						[class.scale-[1.02]]="activeCategory()?.id === category.id">

						<!-- Category Image -->
						<img
							[src]="getCategoryImage(category)"
							[alt]="category.name"
							class="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 ease-out group-hover:scale-110" />

						<!-- Gradient Overlays -->
						<div class="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-80"></div>
						<div class="absolute inset-0 bg-gradient-to-br from-primary/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

						<!-- Active Glow Effect -->
						@if (activeCategory()?.id === category.id) {
							<div class="absolute inset-0 bg-secondary/10"></div>
							<div class="absolute top-4 right-4 w-12 h-12 bg-secondary rounded-full flex items-center justify-center shadow-lg animate-pulse">
								<svg class="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
									<path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
								</svg>
							</div>
						}

						<!-- Content -->
						<div class="absolute bottom-0 left-0 right-0 p-8">
							<!-- Decorative Line -->
							<div class="flex items-center gap-3 mb-4 transition-all duration-500 opacity-0 group-hover:opacity-100 -translate-y-4 group-hover:translate-y-0"
								[class.opacity-100]="activeCategory()?.id === category.id"
								[class.translate-y-0]="activeCategory()?.id === category.id">
								<div class="w-10 h-1 bg-secondary rounded-full"></div>
								<span class="text-secondary text-xs font-bold tracking-[0.2em] uppercase">Explore Now</span>
							</div>

							<!-- Category Name -->
							<h3 class="font-cenzo text-3xl md:text-4xl font-bold text-white mb-3 transition-transform duration-500 group-hover:-translate-y-2">
								{{ category.name }}
							</h3>

							<!-- Description -->
							<p class="text-white/70 text-sm leading-relaxed line-clamp-2 mb-5 transition-all duration-500 opacity-0 group-hover:opacity-100"
								[class.opacity-100]="activeCategory()?.id === category.id">
								{{ category.description }}
							</p>

							<!-- Footer -->
							<div class="flex items-center justify-between">
								<span class="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white text-sm font-semibold">
									{{ category.services.length }} Services
								</span>
								<div class="w-12 h-12 rounded-full bg-secondary/20 border border-secondary/40 flex items-center justify-center transition-all duration-300 group-hover:bg-secondary group-hover:scale-110"
									[class.bg-secondary]="activeCategory()?.id === category.id">
									<svg class="w-5 h-5 transition-colors duration-300"
										[class.text-secondary]="activeCategory()?.id !== category.id"
										[class.text-white]="activeCategory()?.id === category.id"
										fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"/>
									</svg>
								</div>
							</div>
						</div>

						<!-- Corner Accents -->
						<div class="absolute top-6 left-6 w-16 h-16 border-t-2 border-l-2 border-white/20 transition-all duration-500 group-hover:border-secondary/60 group-hover:w-20 group-hover:h-20"></div>
						<div class="absolute bottom-6 right-6 w-16 h-16 border-b-2 border-r-2 border-white/20 transition-all duration-500 group-hover:border-secondary/60 group-hover:w-20 group-hover:h-20"></div>
					</button>
				}
			</div>
		}
	</div>
</section>

<!-- Services Showcase Section -->
<section id="services-section" class="relative py-20 md:py-28 bg-white overflow-hidden">
	<!-- Decorative Background -->
	<div class="absolute top-0 right-0 w-[600px] h-[600px] bg-secondary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
	<div class="absolute bottom-0 left-0 w-[400px] h-[400px] bg-primary/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>

	<div class="relative max-w-[1600px] mx-auto px-6 md:px-16 lg:px-20">
		@if (activeCategory()) {
			<!-- Section Header -->
			<div class="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
				<div>
					<div class="flex items-center gap-3 mb-4">
						<div class="w-12 h-1.5 bg-secondary rounded-full"></div>
						<span class="text-secondary text-sm font-bold tracking-[0.2em] uppercase">{{ activeCategory()?.name }}</span>
					</div>
					<h2 class="font-cenzo text-4xl md:text-5xl font-bold text-primary mb-3">
						Available Services
					</h2>
					<p class="text-charcoal/60 text-lg max-w-xl">
						{{ activeCategory()?.description }}
					</p>
				</div>
				<div class="flex items-center gap-4">
					<span class="px-6 py-3 bg-primary/10 text-primary rounded-full font-bold">
						{{ activeCategory()?.services?.length }} Services
					</span>
				</div>
			</div>

			<!-- Services Horizontal Scroll -->
			<div class="relative group/scroll">
				<!-- Left Scroll Button -->
				@if (canScrollLeft()) {
				<button
					(click)="scrollServices('left')"
					class="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-12 h-12 rounded-full bg-white/20 backdrop-blur-xl border border-white/30 shadow-xl flex items-center justify-center hover:bg-white/40 hover:scale-110 transition-all duration-300 cursor-pointer">
					<svg class="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
					</svg>
				</button>
				}

				<!-- Right Scroll Button -->
				@if (canScrollRight()) {
				<button
					(click)="scrollServices('right')"
					class="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-12 h-12 rounded-full bg-white/20 backdrop-blur-xl border border-white/30 shadow-xl flex items-center justify-center hover:bg-white/40 hover:scale-110 transition-all duration-300 cursor-pointer">
					<svg class="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
					</svg>
				</button>
				}

				<!-- Fade Edges -->
				@if (canScrollLeft()) {
				<div class="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-white to-transparent z-[5] pointer-events-none"></div>
				}
				@if (canScrollRight()) {
				<div class="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-white to-transparent z-[5] pointer-events-none"></div>
				}

				<!-- Scrollable Container -->
				<div
					#servicesScrollContainer
					(scroll)="onServicesScroll()"
					class="flex gap-8 overflow-x-auto pb-6 scroll-smooth scrollbar-hide px-2">
					@for (service of activeCategory()?.services; track service.id; let idx = $index) {
						<div
							class="group flex-shrink-0 w-[380px] md:w-[420px] bg-white/60 backdrop-blur-xl border border-white/40 rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl hover:shadow-primary/10 transition-all duration-500 hover:-translate-y-2"
							(mouseenter)="setHoveredService(service)"
							(mouseleave)="setHoveredService(null)">

							<!-- Image Section -->
							<div class="relative h-[240px] overflow-hidden">
								<img
									[src]="getImageUrl(service.image_path)"
									[alt]="service.service_name"
									class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
									(error)="$event.target.src = '/images/service-placeholder.jpg'" />

								<!-- Gradient Overlay -->
								<div class="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>

								<!-- Number Badge -->
								<div class="absolute top-5 left-5 w-11 h-11 bg-white/90 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-md">
									<span class="font-cenzo text-lg font-bold text-primary">{{ (idx + 1) < 10 ? '0' + (idx + 1) : idx + 1 }}</span>
								</div>

								<!-- Tagline Badge -->
								@if (service.tagline) {
								<div class="absolute top-5 right-5 px-3 py-1.5 bg-secondary/90 backdrop-blur-sm rounded-full">
									<span class="text-xs font-bold text-white uppercase tracking-wide">{{ service.tagline }}</span>
								</div>
								}
							</div>

							<!-- Content Section -->
							<div class="p-6 flex flex-col gap-4">
								<h3 class="font-cenzo text-xl font-bold text-charcoal group-hover:text-primary transition-colors duration-300 line-clamp-1">
									{{ service.service_name }}
								</h3>
								<p class="text-charcoal/60 text-sm leading-relaxed line-clamp-2">
									{{ service.short_description || service.description }}
								</p>

								<!-- Details Pills -->
								<div class="flex flex-wrap gap-2">
									<div class="flex items-center gap-1.5 px-3 py-1.5 bg-primary/5 rounded-full">
										<svg class="w-3.5 h-3.5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
										</svg>
										<span class="text-xs font-medium text-charcoal">{{ service.duration_formatted || service.duration_minutes + ' mins' }}</span>
									</div>
								</div>

								<!-- Footer -->
								<div class="flex items-center justify-between pt-4 border-t border-charcoal/10">
									@if (service.pricing > 0) {
										<div>
											<span class="text-[10px] text-charcoal/40 uppercase tracking-wider font-medium">Starting from</span>
											<div class="text-2xl font-bold text-primary">₹{{ service.pricing | number }}</div>
										</div>
									}
									<button
										(click)="openServiceDetail(service)"
										class="flex items-center gap-1.5 px-5 py-2.5 bg-primary text-white rounded-full text-sm font-semibold hover:bg-secondary transition-all duration-300">
										<span>Details</span>
										<svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
										</svg>
									</button>
								</div>
							</div>
						</div>
					}
				</div>
			</div>
		} @else if (!isLoading()) {
			<!-- No Category Selected State -->
			<div class="text-center py-20">
				<div class="w-24 h-24 bg-secondary/10 rounded-full flex items-center justify-center mx-auto mb-6">
					<svg class="w-12 h-12 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/>
					</svg>
				</div>
				<h3 class="font-cenzo text-2xl font-bold text-charcoal mb-2">Select a Category</h3>
				<p class="text-charcoal/60">Choose a treatment category above to view available services</p>
			</div>
		}
	</div>
</section>

<!-- Why Choose Us Section -->
<section class="relative py-20 md:py-28 bg-primary overflow-hidden">
	<!-- Decorative Elements -->
	<div class="absolute inset-0 overflow-hidden pointer-events-none">
		<div class="absolute -top-32 -right-32 w-96 h-96 bg-secondary/10 rounded-full blur-3xl"></div>
		<div class="absolute -bottom-32 -left-32 w-[500px] h-[500px] bg-white/5 rounded-full blur-3xl"></div>
	</div>

	<!-- Pattern Overlay -->
	<div class="absolute inset-0 opacity-[0.03]" style="background-image: radial-gradient(circle at 1px 1px, white 1px, transparent 0); background-size: 30px 30px;"></div>

	<div class="relative max-w-[1600px] mx-auto px-6 md:px-16 lg:px-20">
		<div class="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
			<!-- Left Content -->
			<div>
				<div class="flex items-center gap-3 mb-6">
					<div class="w-12 h-1 bg-secondary rounded-full"></div>
					<span class="text-secondary text-sm font-bold tracking-[0.2em] uppercase">{{ whyChooseTagline() }}</span>
				</div>
				<h2 class="font-cenzo text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
					{{ whyChooseTitleLine1() }}
					<span class="text-secondary">{{ whyChooseTitleLine2() }}</span>
				</h2>
				<p class="text-white/70 text-lg leading-relaxed mb-10">
					{{ whyChooseDescription() }}
				</p>

				<!-- Features List -->
				<div class="space-y-6">
					<div class="flex items-start gap-4">
						<div class="w-14 h-14 bg-secondary/20 rounded-2xl flex items-center justify-center flex-shrink-0">
							<svg class="w-7 h-7 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
							</svg>
						</div>
						<div>
							<h4 class="text-white font-bold text-lg mb-1">{{ whyChooseFeature1Title() }}</h4>
							<p class="text-white/60 text-sm">{{ whyChooseFeature1Desc() }}</p>
						</div>
					</div>
					<div class="flex items-start gap-4">
						<div class="w-14 h-14 bg-secondary/20 rounded-2xl flex items-center justify-center flex-shrink-0">
							<svg class="w-7 h-7 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"/>
							</svg>
						</div>
						<div>
							<h4 class="text-white font-bold text-lg mb-1">{{ whyChooseFeature2Title() }}</h4>
							<p class="text-white/60 text-sm">{{ whyChooseFeature2Desc() }}</p>
						</div>
					</div>
					<div class="flex items-start gap-4">
						<div class="w-14 h-14 bg-secondary/20 rounded-2xl flex items-center justify-center flex-shrink-0">
							<svg class="w-7 h-7 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>
							</svg>
						</div>
						<div>
							<h4 class="text-white font-bold text-lg mb-1">{{ whyChooseFeature3Title() }}</h4>
							<p class="text-white/60 text-sm">{{ whyChooseFeature3Desc() }}</p>
						</div>
					</div>
				</div>
			</div>

			<!-- Right - Stats/Image -->
			<div class="relative">
				<div class="grid grid-cols-2 gap-6">
					<div class="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
						<div class="text-5xl font-cenzo font-bold text-secondary mb-2">{{ whyChooseStat1Value() }}</div>
						<div class="text-white/80 font-medium">{{ whyChooseStat1Label() }}</div>
					</div>
					<div class="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/10 mt-8">
						<div class="text-5xl font-cenzo font-bold text-secondary mb-2">{{ whyChooseStat2Value() }}</div>
						<div class="text-white/80 font-medium">{{ whyChooseStat2Label() }}</div>
					</div>
					<div class="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
						<div class="text-5xl font-cenzo font-bold text-secondary mb-2">{{ whyChooseStat3Value() }}</div>
						<div class="text-white/80 font-medium">{{ whyChooseStat3Label() }}</div>
					</div>
					<div class="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/10 mt-8">
						<div class="text-5xl font-cenzo font-bold text-secondary mb-2">{{ whyChooseStat4Value() }}</div>
						<div class="text-white/80 font-medium">{{ whyChooseStat4Label() }}</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</section>

<!-- CTA Section -->
<section class="relative py-24 md:py-32 overflow-hidden">
	<!-- Background Image -->
	<div class="absolute inset-0">
		<img src="images/cta-bg.jpg" alt="CTA Background" class="w-full h-full object-cover" />
	</div>
	<div class="absolute inset-0 bg-gradient-to-r from-charcoal/95 via-primary/90 to-charcoal/95"></div>

	<!-- Decorative Elements -->
	<div class="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-secondary/50 to-transparent"></div>
	<div class="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-secondary/50 to-transparent"></div>

	<div class="relative max-w-4xl mx-auto px-6 text-center">
		<!-- Decorative Icon -->
		<div class="w-20 h-20 bg-secondary/20 rounded-full flex items-center justify-center mx-auto mb-8 border border-secondary/30">
			<svg class="w-10 h-10 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
				<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
			</svg>
		</div>

		<h2 class="font-cenzo text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
			Ready to Start Your
			<span class="block mt-2 text-secondary">Transformation Journey?</span>
		</h2>
		<p class="text-white/70 text-lg md:text-xl mb-12 max-w-2xl mx-auto">
			Book your free consultation today and take the first step towards the confident, new you. Our experts are ready to create your personalized treatment plan.
		</p>

		<div class="flex flex-col sm:flex-row items-center justify-center gap-5">
			<button (click)="openBookingModal()" class="w-full sm:w-auto group px-10 py-5 bg-secondary text-primary rounded-full font-bold text-lg tracking-wide shadow-2xl hover:shadow-secondary/30 hover:scale-105 transition-all duration-300">
				<span class="flex items-center justify-center gap-3">
					<svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
					</svg>
					Book Consultation
				</span>
			</button>
			<a [href]="'tel:' + heroPhone()"
				class="w-full sm:w-auto px-10 py-5 bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white rounded-full font-bold text-lg hover:bg-white/20 transition-all duration-300 flex items-center justify-center gap-3">
				<svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
				</svg>
				Call Us
			</a>
		</div>
	</div>
</section>

<!-- Empty State -->
@if (!isLoading() && categories().length === 0) {
	<section class="py-32 px-8 bg-mist">
		<div class="max-w-xl mx-auto text-center">
			<div class="w-28 h-28 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-8">
				<svg class="w-14 h-14 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
				</svg>
			</div>
			<h3 class="font-cenzo text-4xl font-bold text-primary mb-4">Services Coming Soon</h3>
			<p class="text-charcoal/60 text-lg mb-10">
				We're preparing our premium services for you. Check back soon for an amazing experience!
			</p>
			<a routerLink="/" class="inline-flex items-center gap-3 px-10 py-5 bg-primary text-white rounded-full font-bold text-lg hover:bg-secondary transition-colors duration-300">
				<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
				</svg>
				<span>Back to Home</span>
			</a>
		</div>
	</section>
}

<!-- Booking Modal -->
<app-booking-modal
	*ngIf="showBookingModal()"
	(close)="closeBookingModal()"
/>
